import { CustomInput } from "features/CenterContent/shared/operationalInformation/components/CustomInput";
import { CustomWarningText } from "features/CenterContent/shared/operationalInformation/components/CustomWarningText";
import { SeverityRadio } from "features/CenterContent/shared/operationalInformation/components/SeverityRadio";
import {
  EventType,
  GenericType,
} from "features/CenterContent/shared/operationalInformation/hooks/useOperationalInformationTypes";
import { isCustomInputType } from "features/CenterContent/shared/operationalInformation/utils";
import { useEffect } from "react";
import { FieldPath, FieldValues, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { RenderErrorInPath } from "shared/components/forms/RenderErrorInPath";
import { Select } from "shared/components/forms/Select";
import { Flex, Stack } from "shared/components/Spor";
import { useLanguagesForDropsRole } from "shared/hooks/useLanguagesForDropsRole";
import { Language } from "shared/types/language";
import { EndsWith } from "shared/utils/objectUtils";
import { useTheme } from "styled-components";

type TitlePlaceholders = {
  [K in Language]: string;
};

type CustomInputProps<T extends FieldValues> = {
  items: GenericType[] | EventType[];
  disabled?: boolean;
  optionDisabledPredicate?: (event: EventType | GenericType) => boolean;
  showWarningText?: boolean; // This warning texts mentions that they should write `reason` as well
  hasTitleAndDescription?: boolean;
  titlePlaceholders?: TitlePlaceholders;

  label: string;
  typeFormField: FieldPath<T> & EndsWith<"type" | "stationInformationType">;
  severityFormField?: FieldPath<T> & EndsWith<"severity">;
  customFormField: FieldPath<T> & EndsWith<"texts" | "description">;
};

// T is used here in order to typecheck each FormField. Checking whether they are a path of T.
export const FormFieldWithCustomInput = <T extends FieldValues>({
  items,
  disabled = false,
  optionDisabledPredicate = () => false,
  showWarningText,
  hasTitleAndDescription,
  titlePlaceholders,

  label,
  typeFormField,
  severityFormField,
  customFormField,
}: CustomInputProps<T>) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const {
    register,
    getValues,
    formState: { errors },
    unregister,
  } = useFormContext<T>();

  const type = getValues(typeFormField);
  const isCustomInput = isCustomInputType(type);

  const select = (
    <Flex w="100%" flexDir="column" gap="0" position="relative">
      <Select
        key={typeFormField} // Necessary in order for BatchTrainFormField to trigger rerender when switching trainSeries
        data-testid={`select_${label}`}
        label={label}
        placeholder={`${t("Velg en")} ${label.toLowerCase().replace("*", "")}`}
        {...register(typeFormField)}
        disabled={disabled}
      >
        {items.map((item) => (
          <option
            key={item.type}
            value={item.type}
            disabled={optionDisabledPredicate(item)}
          >
            {item.label}
          </option>
        ))}
      </Select>
      <RenderErrorInPath errors={errors} errorPath={typeFormField} />
    </Flex>
  );

  const languages = useLanguagesForDropsRole();

  /**
   * This is necessary if you are switching from "Egendefinert årsak" to any other reason.
   * As fields like ENG: z.string().min(1).optional() is set to "" when you render the customInput,
   * and stays when you switch reason. Thus we need to clear them to `undefined` for them to be valid.
   *
   * Note: This isn't required in "Egendefinert hendelse" as it switches the schema as well when picked.
   */
  useEffect(() => {
    if (!isCustomInput) {
      unregister(customFormField, { keepValue: false });
    }
  }, [isCustomInput]);

  if (!isCustomInput) {
    return select;
  }

  return (
    <Stack
      w="100%"
      gap="12px"
      padding="24px"
      boxShadow={`inset 0 0 0 1px ${theme.colorOutlineMain}`}
      borderRadius="sm"
    >
      {select}
      <Stack gap="12px">
        {severityFormField && (
          <SeverityRadio severityFormField={severityFormField} />
        )}
        {languages.map((language) => (
          <CustomInput
            key={language}
            language={language}
            titlePlaceholder={titlePlaceholders?.[language]}
            hasTitleAndDescription={hasTitleAndDescription}
            customFormField={customFormField}
          />
        ))}
        {showWarningText && <CustomWarningText />}
      </Stack>
    </Stack>
  );
};
